import React from 'react';
import { ConciergeOrderData } from '../types/conversation';
import { getEntityImage } from '../../utils/assetUtils';
import { formatMoney } from '../../utils';
import { FallbackImage } from '../../Components/FallbackImage';
import { Package, Store, CheckCircle, XCircle, Clock, ShoppingBag, Truck } from 'lucide-react';

interface OrderCardProps {
  order: ConciergeOrderData;
}

const StatusBadge: React.FC<{ status: ConciergeOrderData['shipmentStatus'] }> = ({ status }) => {
  const statusConfig: Record<
    ConciergeOrderData['shipmentStatus'],
    { icon: typeof CheckCircle; label: string; bgColor: string; textColor: string; iconColor: string }
  > = {
    DELIVERED: {
      icon: CheckCircle,
      label: 'Delivered',
      bgColor: '#dcfce7',
      textColor: '#166534',
      iconColor: '#16a34a',
    },
    CANCELLED: {
      icon: XCircle,
      label: 'Cancelled',
      bgColor: '#fee2e2',
      textColor: '#991b1b',
      iconColor: '#dc2626',
    },
    IN_TRANSIT: {
      icon: Truck,
      label: 'In Transit',
      bgColor: '#dbeafe',
      textColor: '#1e40af',
      iconColor: '#2563eb',
    },
    PROCESSING: {
      icon: Clock,
      label: 'Processing',
      bgColor: '#fef3c7',
      textColor: '#92400e',
      iconColor: '#d97706',
    },
    UNKNOWN: {
      icon: Clock,
      label: 'Pending',
      bgColor: '#f3f4f6',
      textColor: '#374151',
      iconColor: '#6b7280',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '9999px',
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      <Icon size={14} color={config.iconColor} />
      {config.label}
    </div>
  );
};

const OrderTypeBadge: React.FC<{ orderType?: 'ONLINE' | 'STORE' }> = ({ orderType }) => {
  if (!orderType) return null;

  const isOnline = orderType === 'ONLINE';
  const Icon = isOnline ? Package : Store;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '9999px',
        backgroundColor: '#f3f4f6',
        color: '#374151',
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      <Icon size={14} color="#6b7280" />
      {isOnline ? 'Online' : 'In-Store'}
    </div>
  );
};

export const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const { orderId, orderDate, retailerId, retailerDisplayName, retailerName, shipmentStatus, orderType, products } =
    order;

  const formattedDate = new Date(orderDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const totalAmount = products.reduce((sum, p) => sum + p.retailPrice * p.quantity, 0);
  const totalItems = products.reduce((sum, p) => sum + p.quantity, 0);

  const retailerLogo = order.retailerShortLogo ?? getEntityImage(retailerId, 'retailer');
  const displayName = retailerDisplayName || retailerName;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
      }}
      className="tw:hover:shadow-md"
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={retailerLogo}
            alt={displayName}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              objectFit: 'contain',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
            }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{displayName}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{formattedDate}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <OrderTypeBadge orderType={orderType} />
          <StatusBadge status={shipmentStatus} />
        </div>
      </div>

      {/* Products */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {products.slice(0, 3).map((product, idx) => (
            <div key={`${product.retailerSku}-${idx}`} style={{ display: 'flex', gap: '12px' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {product.imageUrl ? (
                  <FallbackImage
                    src={product.imageUrl}
                    fallbackImage={product.imageUrl.replace('media.brandclub.com', 'media-api.brandclub.com')}
                    alt={product.title || 'Product'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <ShoppingBag size={24} color="#9ca3af" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#111827',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: '1.4',
                  }}
                >
                  {product.title || 'Product'}
                </div>
                {product.brandName && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{product.brandName}</div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '4px',
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                    {formatMoney(product.retailPrice)}
                  </span>
                  {product.quantity > 1 && (
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>x{product.quantity}</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {products.length > 3 && (
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                textAlign: 'center',
                padding: '8px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
              }}
            >
              +{products.length - 3} more item{products.length - 3 > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {totalItems} item{totalItems !== 1 ? 's' : ''}
        </div>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Total: {formatMoney(totalAmount)}</div>
      </div>
    </div>
  );
};

interface OrderCardListProps {
  orders: ConciergeOrderData[];
}

export const OrderCardList: React.FC<OrderCardListProps> = ({ orders }) => {
  if (!orders || orders.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
      {orders.map((order, idx) => (
        <OrderCard key={`${order.orderId}-${order.retailerId}-${idx}`} order={order} />
      ))}
    </div>
  );
};
